import { Fragment, type ReactNode } from 'react';
import { GLOSSARY, LABEL_FIRST } from '../content/glossary';
import { MathTex } from './MathTex';
import { Term } from './Term';

const TOKEN = /(\{\{(?:(?!\}\}).)+\}\}|\$[^$]+\$|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
const LINK = /^\[([^\]]+)\]\(([^)]+)\)$/;

/** Renders inline markup: {{term|text}}, $math$, **bold**, [text](url). */
export function parseMarkup(text: string, keyPrefix = 'm'): ReactNode[] {
  const parts = text.split(TOKEN).filter((p) => p !== '');
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (part.startsWith('{{') && part.endsWith('}}')) {
      const inner = part.slice(2, -2);
      const bar = inner.indexOf('|');
      const id = bar === -1 ? inner : inner.slice(0, bar);
      const shown = bar === -1 ? undefined : inner.slice(bar + 1);
      const entry = GLOSSARY[id.trim()];
      if (!entry) return <Fragment key={key}>{shown ?? id}</Fragment>;
      return (
        <Term key={key} id={entry.id}>
          {shown ? parseMarkup(shown, key) : entry.tex && !LABEL_FIRST.has(entry.id) ? <MathTex tex={entry.tex} /> : entry.label}
        </Term>
      );
    }
    if (part.startsWith('$') && part.endsWith('$') && part.length > 2) return <MathTex key={key} tex={part.slice(1, -1)} />;
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={key}>{parseMarkup(part.slice(2, -2), key)}</strong>;
    const link = part.match(LINK);
    if (link) {
      const [, label, href] = link;
      const external = /^https?:\/\//i.test(href);
      return (
        <a key={key} href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
          {parseMarkup(label, key)}
        </a>
      );
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}

/** Plain-text form of markup for aria labels and summaries. */
export function stripMarkup(text: string): string {
  return text
    .replace(/\{\{([^}|]+)(?:\|((?:(?!\}\}).)+))?\}\}/g, (_, id: string, shown?: string) => shown ?? id)
    .replace(/\$([^$]+)\$/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
}

export function Markup({ text, as: Tag = 'span' }: { text: string; as?: 'span' | 'p' | 'div' | 'li' }) {
  return <Tag>{parseMarkup(text)}</Tag>;
}
